import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

import DownChevronMedium from './icons/DownChevronMedium';
import {pageHeaderStyles} from './pageHeaderStyles';

/**
 * The level of the page headings inside it. A dashboard page's heading is an
 * `h2` below the `h1` of the dashboard, which names the podcast, episode,
 * network or clip. The account dashboard names nothing, so it provides `1` and
 * the heading of each of its pages is the page's `h1`, with the same look.
 */
export const PageHeadingLevelContext = React.createContext<1 | 2>(2);

const HeadingH2 = styled('h2', (_, designColors) =>
  pageHeaderStyles(designColors),
);
const HeadingH1 = styled('h1', (_, designColors) =>
  pageHeaderStyles(designColors),
);

const Trigger = styled(
  'button',
  (_: unknown, designColors) => ({
    alignItems: 'center',
    background: 'none',
    border: 0,
    borderRadius: 4,
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    font: 'inherit',
    gap: 8,
    letterSpacing: 'inherit',
    margin: 0,
    padding: 0,
    textAlign: 'left',
    ...focusVisibleRing(designColors),
  }),
  {type: 'button'},
);

const useHeading = () =>
  React.useContext(PageHeadingLevelContext) === 1 ? HeadingH1 : HeadingH2;

/**
 * The heading of a page that is outside a dashboard's sidebar. It shows at
 * every width. It used to hide below 600px and leave the narrow-screen
 * navigation `<select>` to stand in for it, so on a phone the page had no
 * heading.
 *
 * The pages in a dashboard's sidebar have no heading of their own:
 * `ChromeSidebar` renders it above the page, from the page's link
 * (`headingMessage`), so that it is the same while the page loads.
 */
const DesktopPageHeader = ({
  children,
  ...rest
}: React.ComponentProps<typeof HeadingH2>) => {
  const Heading = useHeading();
  return <Heading {...rest}>{children}</Heading>;
};

/**
 * The drawer that holds a dashboard's links on a phone (`ChromeSidebar`):
 * what it is called, whether it is open, and how to open it.
 */
export type PageHeadingMenu = {
  /** The drawer's name, such as "Podcast menu". It describes the button. */
  label: string;
  onOpen: () => void;
  open: boolean;
};

/**
 * A page heading whose text is also the button that opens the page's
 * navigation drawer (`narrow-nav-select`, prompt U2). It stays a heading: the
 * button sits inside the `h2` (or the `h1`, see `PageHeadingLevelContext`), as
 * in the WAI-ARIA accordion pattern, so heading navigation still finds
 * "Episode list". The drawer's name ("Podcast menu") describes the button
 * without being part of the heading's own name.
 */
export const MenuPageHeader = ({
  children,
  menu,
  ...rest
}: React.ComponentProps<typeof HeadingH2> & {menu: PageHeadingMenu}) => {
  const Heading = useHeading();
  const hintID = `${React.useId()}hint`;
  return (
    <Heading {...rest}>
      <Trigger
        aria-describedby={hintID}
        aria-expanded={menu.open}
        aria-haspopup="dialog"
        onClick={menu.onOpen}
      >
        <span>{children}</span>
        <DownChevronMedium size={14} />
      </Trigger>
      <span hidden id={hintID}>
        {menu.label}
      </span>
    </Heading>
  );
};

export default DesktopPageHeader;
